import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { isRTLAtom, cultureAtom, isAuthenticatedAtom } from './store';
import { authLoadingAtom } from './store/atoms/authAtoms';
import { AuthenticatedRegistrationRoute } from './components/AuthenticatedRegistrationRoute';

// Pages - Lazy loaded
const Unauthorized = React.lazy(() => import('./pages/auth/Unauthorized'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const AuthCallback = React.lazy(() => import('./auth/AuthCallback'));
const Register = React.lazy(() => import('./pages/registration/Register'));
const VerifyEmail = React.lazy(() => import('./pages/registration/VerifyEmail'));
const PendingApproval = React.lazy(() => import('./pages/registration/PendingApproval'));
const AcceptInvitation = React.lazy(() => import('./pages/auth/AcceptInvitation'));
const AcceptHandoff = React.lazy(() => import('./pages/auth/AcceptHandoff'));
const UploadWithToken = React.lazy(() => import('./pages/UploadWithToken'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const DataRequests = React.lazy(() => import('./pages/DataRequests'));
const Documents = React.lazy(() => import('./pages/Documents'));
const MessagesPage = React.lazy(() => import('./pages/messages/MessagesPage'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Profile = React.lazy(() => import('./pages/Profile'));
const SubmitRequest = React.lazy(() => import('./pages/requests/SubmitRequest'));
const MyRequests = React.lazy(() => import('./pages/requests/MyRequests'));
const MyRequestDetail = React.lazy(() => import('./pages/requests/MyRequestDetail'));

// Layout
const Layout = React.lazy(() => import('./layouts/Layout'));

// Layout styles
const useLayoutStyles = makeStyles({
  app: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

// Loading fallback
const LoadingFallback = () => {
  const layoutStyles = useLayoutStyles();
  // useSuspense: false prevents an infinite loop when this component is itself a Suspense fallback
  const { t } = useTranslation('portal-common', { useSuspense: false });
  return (
    <div className={layoutStyles.loadingContainer}>
      <Spinner size="extra-large" label={t('loading', 'Loading...')} />
    </div>
  );
};

// Protected Route component — waits for auth initialisation before redirecting.
// Without the loading gate, ProtectedRoute redirects to /login before
// AuthProvider has resolved the OIDC session from the callback.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const isAuthLoading = useAtomValue(authLoadingAtom);

  if (isAuthLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const isRTL = useAtomValue(isRTLAtom);
  const setCulture = useSetAtom(cultureAtom);
  const { i18n } = useTranslation();
  const layoutStyles = useLayoutStyles();

  // Sync i18next language with cultureAtom on mount and when language changes
  React.useEffect(() => {
    if (i18n.language) {
      setCulture(i18n.language);
    }

    const handleLanguageChange = (lng: string) => {
      setCulture(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, setCulture]);

  // Update document direction when RTL changes
  React.useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <div className={layoutStyles.app}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/register"
            element={
              <AuthenticatedRegistrationRoute>
                <Register />
              </AuthenticatedRegistrationRoute>
            }
          />
          <Route path="/pending" element={<PendingApproval />} />
          <Route path="/register/verify-email" element={<VerifyEmail />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/auth/accept-handoff" element={<AcceptHandoff />} />
          <Route path="/upload-documents/:token" element={<UploadWithToken />} />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/data-requests" element={<DataRequests />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/submit-request" element={<SubmitRequest />} />
            <Route path="/my-requests" element={<MyRequests />} />
            <Route path="/my-requests/:id" element={<MyRequestDetail />} />
          </Route>

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Suspense>
  );
}

export default App;
